/**
 * Product page depot handling.
 *
 * Reads the product from Shopify's own /products/<handle>.js endpoint rather
 * than scraping the theme's markup, so it works out whether the product is
 * depot-based without depending on theme class names.
 *
 *   no depot chosen         -> block add to cart, prompt for postcode
 *   outside delivery area   -> block add to cart, explain
 *   depot not stocked here  -> block add to cart, explain
 *   otherwise               -> select the depot variant, hide the other depots
 */
(function () {
  'use strict';

  var product = null;
  var depotOptionIndex = -1;   // 0-based position of the depot option
  var depotNames = [];

  /* ------------------------------------------------------------- utilities */

  function isProductPage() {
    return window.location.pathname.indexOf('/products/') > -1;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * The real product form. Themes often render more than one /cart/add form -
   * theme "Next" ships a hidden installments form that contains nothing but
   * hidden inputs - so pick the one that actually has an add-to-cart control.
   */
  function productForm() {
    var forms = [].slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
    if (forms.length === 0) return null;

    var real = forms.filter(function (form) {
      if (/installment/i.test(form.className)) return false;
      return !!form.querySelector('[type="submit"], [name="add"]');
    });

    return real[0] || forms[0];
  }

  function addToCartButton() {
    var form = productForm();
    if (!form) return null;

    return form.querySelector('[type="submit"], [name="add"]') ||
           form.querySelector('button.overlay-buy_button');
  }

  /**
   * Form controls, via form.elements rather than descendant selectors: themes
   * associate inputs with `form="<id>"` instead of nesting them, and theme
   * "Next" does exactly that for its variant selects.
   */
  function formControls(tagName) {
    var form = productForm();
    if (!form) return [];

    return [].slice.call(form.elements).filter(function (el) {
      return el.tagName === tagName;
    });
  }

  function depotSelects() {
    return formControls('SELECT').filter(function (select) {
      return [].some.call(select.options, function (option) {
        return depotNames.indexOf(option.value.trim()) > -1;
      });
    });
  }

  function masterSelect() {
    return formControls('SELECT').filter(function (select) {
      return select.name === 'id';
    })[0] || null;
  }

  /* -------------------------------------------------------------- blocking */

  var blocked = false;

  function block() {
    blocked = true;

    var button = addToCartButton();
    if (button) {
      button.disabled = true;
      button.classList.add('depot-blocked');
    }

    var dynamic = document.querySelector('.shopify-payment-button');
    if (dynamic) dynamic.classList.add('depot-blocked');
  }

  function unblock() {
    blocked = false;

    var button = addToCartButton();
    if (button) {
      button.disabled = false;
      button.classList.remove('depot-blocked');
    }

    var dynamic = document.querySelector('.shopify-payment-button');
    if (dynamic) dynamic.classList.remove('depot-blocked');
  }

  // Belt and braces: themes re-render buttons, and disabled attributes get
  // wiped. Stop the submit itself while we're in a blocked state.
  document.addEventListener('submit', function (e) {
    if (blocked && e.target.matches('form[action*="/cart/add"]')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  /* --------------------------------------------------------------- notices */

  function clearNotices() {
    document.querySelectorAll('.depot-notice, .depot-banner').forEach(function (el) {
      el.remove();
    });
  }

  function insertNotice(html, className) {
    var form = productForm();
    if (!form) return;

    var notice = document.createElement('div');
    notice.className = className;
    notice.innerHTML = html;
    form.insertBefore(notice, form.firstChild);
  }

  function showNeedsPostcode() {
    insertNotice(
      '<p><strong>Please enter your postcode first</strong></p>' +
      '<p>We need to know your delivery depot before you can order.</p>' +
      '<button type="button" class="depot-notice__action" data-depot-checker-open>Enter postcode</button>',
      'depot-notice depot-notice--required'
    );
  }

  function showOutsideArea() {
    insertNotice(
      '<p><strong>Sorry, we do not deliver to your area</strong></p>' +
      '<p>Your postcode is outside all of our delivery areas, so this order cannot be accepted.</p>' +
      '<button type="button" class="depot-notice__action" data-depot-checker-open>Check a different postcode</button>',
      'depot-notice depot-notice--unavailable'
    );
  }

  function showNotStocked(depot) {
    insertNotice(
      '<p><strong>Not available in your area</strong></p>' +
      '<p>This product is not available for delivery from <strong>' + escapeHtml(depot) + '</strong>.</p>',
      'depot-notice depot-notice--unavailable'
    );
  }

  function showBanner(depot) {
    insertNotice(
      '<span>Delivering from: <strong>' + escapeHtml(depot) + '</strong></span>' +
      '<button type="button" class="depot-banner__change" data-depot-checker-open>Change</button>',
      'depot-banner'
    );
  }

  /* ---------------------------------------------------------- product data */

  function fetchProduct() {
    var handlePath = window.location.pathname.split('?')[0].replace(/\/$/, '');
    return fetch(handlePath + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
  }

  /** Which option (Size / Depot / Colour...) holds the depot names? */
  function findDepotOptionIndex(prod) {
    var options = prod.options || [];

    for (var i = 0; i < options.length; i++) {
      var values = {};
      (prod.variants || []).forEach(function (variant) {
        values[variant.options[i]] = true;
      });

      var matches = Object.keys(values).filter(function (value) {
        return depotNames.indexOf(value) > -1;
      });

      if (matches.length > 0) return i;
    }

    return -1;
  }

  function variantsForDepot(depot) {
    return (product.variants || []).filter(function (variant) {
      return variant.options[depotOptionIndex] === depot;
    });
  }

  /* --------------------------------------------------- variant UI handling */

  /**
   * Select the depot in whatever control the theme renders.
   *
   * Only ever fires a change event when the value is actually wrong - themes
   * re-render the product section on change, which re-triggers our observer,
   * so an unconditional dispatch here would loop forever.
   */
  function selectDepotInUI(depot, variant) {
    var selected = false;

    // Option-level selects, e.g. <select name="options[Depot]">
    depotSelects().forEach(function (select) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value.trim() === depot) {
          if (select.selectedIndex !== i) {
            select.selectedIndex = i;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          selected = true;
          break;
        }
      }
    });

    // Radio / swatch style pickers
    formControls('INPUT').forEach(function (input) {
      if (input.type !== 'radio' || input.value !== depot) return;
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      selected = true;
    });

    // Master variant select, where the theme uses one
    var master = masterSelect();
    if (master && variant && String(master.value) !== String(variant.id)) {
      for (var j = 0; j < master.options.length; j++) {
        if (String(master.options[j].value) === String(variant.id)) {
          master.selectedIndex = j;
          master.dispatchEvent(new Event('change', { bubbles: true }));
          selected = true;
          break;
        }
      }
    }

    // Hidden id input used by some themes / page builders
    var hiddenId = formControls('INPUT').filter(function (el) { return el.name === 'id'; })[0];
    if (hiddenId && variant && hiddenId.type === 'hidden') hiddenId.value = variant.id;

    return selected;
  }

  /** Hide every depot the customer can't order from. */
  function hideOtherDepots(depot) {
    function isOtherDepot(text) {
      var value = String(text || '').trim();
      return depotNames.indexOf(value) > -1 && value !== depot;
    }

    formControls('SELECT').forEach(function (select) {
      [].forEach.call(select.options, function (option) {
        if (isOtherDepot(option.value) || isOtherDepot(option.textContent)) {
          option.disabled = true;
          option.hidden = true;
        }
      });
    });

    // A master <select name="id"> lists whole variants ("Gamrie / 25kg"), so
    // the exact-match test above never fires. Resolve those by variant id.
    var byId = {};
    (product.variants || []).forEach(function (v) { byId[String(v.id)] = v; });

    var master = masterSelect();
    if (master) {
      [].forEach.call(master.options, function (option) {
        var variant = byId[String(option.value)];
        if (variant && isOtherDepot(variant.options[depotOptionIndex])) {
          option.disabled = true;
          option.hidden = true;
        }
      });
    }

    formControls('INPUT').forEach(function (input) {
      if (input.type !== 'radio' || !isOtherDepot(input.value)) return;
      input.disabled = true;
      var label = input.closest('label') ||
                  (input.id ? document.querySelector('label[for="' + input.id + '"]') : null);
      if (label) label.style.display = 'none';
      else input.style.display = 'none';
    });

    // Custom dropdown UI ("semantic select") that this theme renders on top of
    // the native selects - the native ones are hidden, so these are what the
    // customer actually sees and clicks.
    document.querySelectorAll('.bv_mainselect .bv_ul_inner .li').forEach(function (item) {
      var textEl = item.querySelector('.text');
      if (textEl && isOtherDepot(textEl.textContent)) item.style.display = 'none';
    });

    document.querySelectorAll('.bv_mainselect .bv_atual .sl_inner').forEach(function (display) {
      if (isOtherDepot(display.textContent)) display.textContent = depot;
    });
  }

  /**
   * Keep ?variant= in step with the selection without reloading the page.
   * The reference site does a full redirect here; replaceState avoids the
   * reload loop that causes when a theme rewrites the query string.
   */
  function syncUrl(variant) {
    if (!variant || !window.history || !window.history.replaceState) return;

    var params = new URLSearchParams(window.location.search);
    if (params.get('variant') === String(variant.id)) return;

    params.set('variant', variant.id);
    window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
  }

  /* -------------------------------------------------- re-render resilience */

  var observer = null;
  var reapplyTimer = null;

  /**
   * Themes re-render the product section after a variant change (theme "Next"
   * refetches and swaps the whole block ~1-2s later), which throws away our
   * banner and un-hides the depot options. Watch for that and re-apply.
   *
   * The observer is disconnected while we make our own changes, otherwise
   * apply() would retrigger itself forever.
   */
  function watchRerender() {
    var section = document.querySelector('[id^="MainProduct"], [id*="main-product"], main') || document.body;

    observer = new MutationObserver(function () {
      clearTimeout(reapplyTimer);
      reapplyTimer = setTimeout(apply, 250);
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ main */

  function apply() {
    if (observer) observer.disconnect();
    try {
      applyInner();
    } finally {
      // Reconnect after the current task so our own writes don't queue records.
      setTimeout(function () {
        if (observer) {
          var section = document.querySelector('[id^="MainProduct"], [id*="main-product"], main') || document.body;
          observer.observe(section, { childList: true, subtree: true });
        }
      }, 0);
    }
  }

  function applyInner() {
    clearNotices();
    unblock();

    if (depotOptionIndex === -1) return;  // not a depot-based product

    var stateName = window.DepotChecker.state();

    if (stateName === 'unset') {
      block();
      showNeedsPostcode();
      return;
    }

    if (stateName === 'outside') {
      block();
      showOutsideArea();
      return;
    }

    var depot = window.DepotChecker.get();
    var matching = variantsForDepot(depot);

    if (matching.length === 0) {
      block();
      showNotStocked(depot);
      showBanner(depot);
      return;
    }

    var variant = matching.filter(function (v) { return v.available; })[0] || matching[0];

    selectDepotInUI(depot, matching.length === 1 ? variant : null);
    hideOtherDepots(depot);
    syncUrl(matching.length === 1 ? variant : null);
    showBanner(depot);
  }

  function init() {
    if (!isProductPage() || !window.DepotChecker) return;

    // Block first, ask questions later - avoids a window where the customer
    // can add to cart before the coverage data has arrived.
    block();

    window.DepotChecker.ready.then(function (lookup) {
      if (!lookup) {
        unblock();   // data failed to load: don't hold the shop hostage
        return;
      }

      depotNames = window.DepotChecker.depotNames();

      return fetchProduct().then(function (prod) {
        product = prod;
        depotOptionIndex = findDepotOptionIndex(prod);
        apply();
        window.DepotChecker.onChange(apply);
        if (depotOptionIndex > -1) watchRerender();
      });
    }).catch(function (err) {
      console.error('[depot-checker] product setup failed', err);
      unblock();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
